import { useEffect, useRef, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchRecentBulkJobs, type BulkJobDto } from '@/api/queries/bulkJobs';
import { fetchRecentImportJobs, type ImportJobDto } from '@/api/queries/importJobs';
import { useTenantStore } from '@/auth/tenantStore';
import { useToast } from '@/components/ui';

import { startJobsHub, stopJobsHub, type BulkJobNotification } from './jobsHub';
import {
  isBulkJobActive,
  jobLabel,
  useBulkJobsStore,
  type BulkJobInfo,
  type BulkJobStatus,
} from './bulkJobsStore';

function toJobInfo(n: BulkJobNotification | BulkJobDto): BulkJobInfo {
  const id = 'jobId' in n ? n.jobId : n.id;
  return {
    id,
    jobType: 'jobType' in n ? n.jobType : undefined,
    operation: n.operation,
    status: n.status as BulkJobStatus,
    total: n.total,
    processed: n.processed,
    affected: n.affected,
    skipped: n.skipped,
    created: 'created' in n ? n.created : undefined,
    updated: 'updated' in n ? n.updated : undefined,
    failed: 'failed' in n ? n.failed : undefined,
    error: n.error,
  };
}

function importDtoToJobInfo(d: ImportJobDto): BulkJobInfo {
  return {
    id: d.id,
    jobType: 'import',
    operation: d.format,
    status: d.status,
    total: d.total,
    processed: d.processed,
    affected: d.created + d.updated,
    skipped: d.skipped,
    created: d.created,
    updated: d.updated,
    failed: d.errors?.length ?? 0,
    error: d.error,
  };
}

/**
 * Renders nothing; mounted once in the authenticated shell. Connects the
 * SignalR jobs hub for the current tenant, feeds bulkJobsStore (which drives
 * the blocking progress dialog + top-bar JobsIndicator), shows the completion
 * toast with affected counts, and refreshes the contacts list when a job
 * finishes. While the hub is down and a job is active, falls back to polling
 * GET /contacts/_bulk/jobs every 3s (same pattern as syncJobs.ts).
 */
export function BulkJobsListener() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  const { show } = useToast();
  const queryClient = useQueryClient();
  const upsert = useBulkJobsStore((s) => s.upsert);
  const clear = useBulkJobsStore((s) => s.clear);
  const hasActiveJob = useBulkJobsStore((s) =>
    Object.values(s.jobs).some((j) => isBulkJobActive(j.status)),
  );

  const [hubConnected, setHubConnected] = useState(false);
  // Jobs whose completion toast has already fired — the hub push and the
  // polling reconciliation can both observe the same terminal transition.
  const notifiedRef = useRef(new Set<string>());

  const notifyCompleted = (job: BulkJobInfo) => {
    if (notifiedRef.current.has(job.id)) return;
    notifiedRef.current.add(job.id);

    const label = jobLabel(job);
    if (job.status === 'failed') {
      show({
        severity: 'error',
        title: `${label} failed`,
        description: job.error ?? `${job.processed.toLocaleString()} of ${job.total.toLocaleString()} processed`,
        duration: 8000,
      });
    } else if (job.jobType === 'import') {
      const extra = [
        (job.skipped ?? 0) > 0 ? `${job.skipped.toLocaleString()} skipped` : null,
        (job.failed ?? 0) > 0 ? `${(job.failed ?? 0).toLocaleString()} errors` : null,
      ].filter(Boolean).join(', ');
      show({
        severity: 'success',
        title: `${label} finished`,
        description: `${(job.created ?? 0).toLocaleString()} created, ${(job.updated ?? 0).toLocaleString()} updated${extra ? `, ${extra}` : ''}`,
        duration: 8000,
      });
    } else {
      show({
        severity: 'success',
        title: `${label} finished`,
        description: `${job.affected.toLocaleString()} of ${job.total.toLocaleString()} contacts affected${job.skipped > 0 ? `, ${job.skipped.toLocaleString()} skipped` : ''}`,
        duration: 8000,
      });
    }
    void queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
  };

  useEffect(() => {
    if (!tenantId) return;
    clear();
    notifiedRef.current = new Set();

    void startJobsHub(tenantId, {
      onProgress: (n) => upsert(toJobInfo(n)),
      onCompleted: (n) => {
        const job = toJobInfo(n);
        upsert(job);
        notifyCompleted(job);
      },
      onStateChange: setHubConnected,
    });

    return () => {
      setHubConnected(false);
      void stopJobsHub();
    };
    // notifyCompleted/upsert/clear are stable enough (store actions + toast);
    // the connection must only rebuild on tenant switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Seed on mount (re-attach to a job started before a reload / in another tab)
  // and poll while a job is active but the hub is down. Bulk + import jobs share
  // the store, so both kinds are seeded/reconciled the same way.
  const reconcile = (list: BulkJobInfo[]) => {
    const { jobs } = useBulkJobsStore.getState();
    for (const job of list) {
      const known = jobs[job.id];
      if (!isBulkJobActive(job.status)) {
        // Only toast for completions we watched happen — not for old terminal
        // jobs seeded on page load.
        if (known && isBulkJobActive(known.status)) {
          upsert(job);
          notifyCompleted(job);
        } else {
          notifiedRef.current.add(job.id);
        }
        continue;
      }
      upsert(job);
    }
  };

  const { data: recentBulk } = useQuery({
    queryKey: ['bulk-jobs', 'recent', tenantId],
    queryFn: () => fetchRecentBulkJobs(10),
    enabled: !!tenantId,
    refetchInterval: hasActiveJob && !hubConnected ? 3000 : false,
    staleTime: 0,
  });

  const { data: recentImports } = useQuery({
    queryKey: ['import-jobs', 'recent', tenantId],
    queryFn: () => fetchRecentImportJobs(10),
    enabled: !!tenantId,
    refetchInterval: hasActiveJob && !hubConnected ? 3000 : false,
    staleTime: 0,
  });

  useEffect(() => {
    if (recentBulk) reconcile(recentBulk.map(toJobInfo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentBulk]);

  useEffect(() => {
    if (recentImports) reconcile(recentImports.map(importDtoToJobInfo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentImports]);

  return null;
}
